import { useId, type ReactNode } from 'react';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export type StepTone = 'done' | 'todo' | 'attention';

const TONES: Record<StepTone, string> = {
  done: 'bg-success/15 text-success dark:bg-success/20 border-transparent',
  todo: 'border-border text-muted-foreground',
  attention: 'bg-destructive/10 text-destructive dark:bg-destructive/20 border-transparent',
};

export function StepBadge({ tone, children }: { tone: StepTone; children: ReactNode }) {
  return (
    <Badge variant="outline" className={cn(TONES[tone])}>
      {children}
    </Badge>
  );
}

export interface ChannelStepProps {
  index: number;
  title: string;
  badge?: ReactNode;
  /** Greys the step out while an earlier one is not done; its controls stay unmounted. */
  disabled?: boolean;
  children: ReactNode;
}

/** One numbered step on a channel card; a `region` named after its title for tests and readers. */
export function ChannelStep({ index, title, badge, disabled = false, children }: ChannelStepProps) {
  const id = useId();
  return (
    <li
      className={cn('flex gap-3', disabled && 'opacity-60')}
      data-disabled={disabled || undefined}
    >
      <span
        aria-hidden
        className="bg-muted text-muted-foreground flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums"
      >
        {index}
      </span>
      <section aria-labelledby={id} className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 id={id} className="text-sm font-semibold">
            {title}
          </h3>
          {badge}
        </div>
        {children}
      </section>
    </li>
  );
}

export function StepRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
